import { MessageSquareText, Regex, Clock3, Moon, CalendarDays, CheckCircle2, MousePointerClick, Webhook, Megaphone } from "lucide-react";

export const TRIGGERS = {
  keyword: { label: "Messaggio: contiene", icon: MessageSquareText, cls: "bg-[#10B981]/15 text-[#10B981]" },
  exact: { label: "Messaggio: testo esatto", icon: MessageSquareText, cls: "bg-[#8B5CF6]/15 text-[#8B5CF6]" },
  regex: { label: "Messaggio: regex", icon: Regex, cls: "bg-[#06B6D4]/15 text-[#06B6D4]" },
  welcome: { label: "Primo messaggio", icon: Clock3, cls: "bg-[#3B82F6]/15 text-[#3B82F6]" },
  fallback: { label: "Qualsiasi messaggio", icon: MessageSquareText, cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  non_working_hours: { label: "Orari non lavorativi", icon: Moon, cls: "bg-[#6366F1]/15 text-[#6366F1]" },
  date: { label: "Data specifica", icon: CalendarDays, cls: "bg-[#F97316]/15 text-[#F97316]" },
  holiday: { label: "Giorni festivi", icon: CalendarDays, cls: "bg-[#EAB308]/15 text-[#EAB308]" },
  marketing_optin: { label: "Accettazione marketing", icon: CheckCircle2, cls: "bg-[#22C55E]/15 text-[#22C55E]" },
  link_click: { label: "Clic su link", icon: MousePointerClick, cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
  campaign_reply: { label: "Risposta a campagna", icon: Megaphone, cls: "bg-[#0EA5E9]/15 text-[#0EA5E9]" },
  webhook: { label: "Integrazione (Webhook)", icon: Webhook, cls: "bg-[#EC4899]/15 text-[#EC4899]" },
};

export const TRIGGER_GROUPS = [
  { group: "Messaggio dal cliente", items: ["keyword", "exact", "regex"] },
  { group: "Comportamento", items: ["welcome", "fallback", "marketing_optin", "link_click", "campaign_reply"] },
  { group: "Tempo", items: ["non_working_hours", "date", "holiday"] },
  { group: "Integrazione", items: ["webhook"] },
];

export const validateSteps = (steps) => {
  const keys = new Set(steps.map((s) => s.key));
  for (let i = 0; i < steps.length; i++) {
    const opts = steps[i].options || [];
    for (let j = 0; j < opts.length; j++) {
      if (!(opts[j].label || "").trim()) return `Step ${i + 1}: l'opzione ${j + 1} è vuota. Inserisci un testo o rimuovila.`;
      if (opts[j].next_key && !keys.has(opts[j].next_key)) return `Step ${i + 1}: l'opzione ${j + 1} punta a uno step inesistente.`;
      if (opts[j].next_key === steps[i].key && !opts[j].back_to_menu) return `Step ${i + 1}: l'opzione ${j + 1} rimanda allo stesso step (loop).`;
    }
    const act = steps[i].action;
    if (act && act.type && act.type !== "none" && !(act.ref || "").trim()) return `Step ${i + 1}: seleziona l'elemento per l'azione scelta.`;
    const cap = steps[i].capture;
    if (cap && !(cap.field || "").trim()) return `Step ${i + 1}: indica in quale campo salvare la risposta del cliente.`;
    const nk = steps[i].next_key;
    if (nk && !keys.has(nk)) return `Step ${i + 1}: il collegamento "poi →" punta a uno step inesistente.`;
    if (nk && nk === steps[i].key) return `Step ${i + 1}: il collegamento "poi →" rimanda allo stesso step (loop).`;
  }
  return null;
};

export const findCycle = (steps) => {
  const graph = Object.fromEntries(steps.map((s) => [s.key, [
    ...(s.options || []).filter((o) => o.next_key && !o.back_to_menu).map((o) => o.next_key),
    ...(s.capture?.next_key ? [s.capture.next_key] : []),
    ...(s.next_key ? [s.next_key] : []),
  ]]));
  const state = {}; const path = [];
  const dfs = (k) => {
    state[k] = 1; path.push(k);
    for (const n of graph[k] || []) {
      if (!(n in graph)) continue;
      if (state[n] === 1) return [...path.slice(path.indexOf(n)), n];
      if (!state[n]) { const r = dfs(n); if (r) return r; }
    }
    path.pop(); state[k] = 2; return null;
  };
  for (const k of Object.keys(graph)) { if (!state[k]) { const r = dfs(k); if (r) return r; } }
  return null;
};

export const cycleMessage = (steps, cyc) => "Ciclo rilevato: " + cyc.map((k) => `Step ${steps.findIndex((s) => s.key === k) + 1}`).join(" → ");

export const NODE_W = 280;
const COL_GAP = 120;
const ROW_GAP = 40;
const SLOT_H = 190;
export const GRID = 22;
export const snap = (v) => Math.round(v / GRID) * GRID;
export const TRIGGER_POS = { x: 40, y: 48 };
export const TRIGGER_W = 200;

export const outgoingKeys = (s) => [
  ...(s.options || []).map((o) => o.next_key),
  s.next_key, s.capture?.next_key, s.wait?.next_key, s.condition?.yes_key, s.condition?.no_key,
].filter(Boolean);

export function layoutFlow(automation) {
  const steps = automation.steps || [];
  const byKey = Object.fromEntries(steps.map((s) => [s.key, s]));
  const startKey = automation.start_key || steps[0]?.key;
  const depth = {};
  if (startKey) {
    depth[startKey] = 1;
    const q = [startKey];
    while (q.length) {
      const k = q.shift();
      (byKey[k] ? outgoingKeys(byKey[k]) : []).forEach((nk) => {
        if (depth[nk] === undefined && byKey[nk]) { depth[nk] = depth[k] + 1; q.push(nk); }
      });
    }
  }
  steps.forEach((s) => { if (depth[s.key] === undefined) depth[s.key] = 1; });
  const slotByDepth = {};
  const pos = {};
  steps.forEach((s) => {
    const d = depth[s.key];
    slotByDepth[d] = (slotByDepth[d] || 0);
    pos[s.key] = { x: TRIGGER_POS.x + TRIGGER_W + COL_GAP + (d - 1) * (NODE_W + COL_GAP), y: TRIGGER_POS.y + slotByDepth[d] * (SLOT_H + ROW_GAP) };
    slotByDepth[d] += 1;
  });
  return { pos, startKey };
}

export function edgePath(sx, sy, tx, ty) {
  const dx = Math.max(50, Math.abs(tx - sx) / 2);
  return `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
}
